import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Plus, Edit, Trash2, Eye, Calendar, User, Tag, ExternalLink } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { insertCaseStudySchema, type CaseStudy, type InsertCaseStudy } from "@shared/schema";
import { SimpleRichTextEditor } from "@/components/ui/simple-rich-text-editor";
import { apiRequest } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";
import SuperAdminLayout from "@/components/admin/super-admin-layout";

interface CaseStudyFormProps {
  caseStudy?: CaseStudy;
  onSuccess: () => void;
  onCancel: () => void;
}

function CaseStudyForm({ caseStudy, onSuccess, onCancel }: CaseStudyFormProps) {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [tagsInput, setTagsInput] = useState(caseStudy?.tags?.join(", ") || "");
  const { toast } = useToast();
  const queryClient = useQueryClient();
  
  const isEditing = !!caseStudy;

  const form = useForm<InsertCaseStudy>({
    resolver: zodResolver(insertCaseStudySchema),
    defaultValues: {
      title: caseStudy?.title || "",
      slug: caseStudy?.slug || "",
      excerpt: caseStudy?.excerpt || "",
      content: caseStudy?.content || "",
      featuredImage: caseStudy?.featuredImage || "",
      authorName: caseStudy?.authorName || "SAASGAMA Team",
      authorEmail: caseStudy?.authorEmail || "support@saasgama.com",
      isPublished: caseStudy?.isPublished || false,
      tags: caseStudy?.tags || [],
      metaTitle: caseStudy?.metaTitle || "",
      metaDescription: caseStudy?.metaDescription || "",
    },
  });

  const mutation = useMutation({
    mutationFn: async (data: InsertCaseStudy) => {
      // Process tags
      const tags = tagsInput
        .split(",")
        .map(tag => tag.trim())
        .filter(tag => tag.length > 0);

      const payload = { ...data, tags };
      console.log("Sending case study payload:", JSON.stringify(payload, null, 2));

      if (isEditing && caseStudy) {
        return apiRequest(`/api/admin/case-studies/${caseStudy.id}`, { 
          method: "PUT", 
          body: payload 
        });
      } else {
        return apiRequest("/api/admin/case-studies", { 
          method: "POST", 
          body: payload 
        });
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/admin/case-studies"] });
      toast({
        title: isEditing ? "Case study updated" : "Case study created",
        description: `Case study has been ${isEditing ? "updated" : "created"} successfully.`,
      });
      onSuccess();
    },
    onError: (error: any) => {
      toast({
        title: "Error",
        description: error.message || `Failed to ${isEditing ? "update" : "create"} case study`,
        variant: "destructive",
      });
    },
  });

  const onSubmit = async (data: InsertCaseStudy) => {
    console.log("=== FORM SUBMIT DEBUG START ===");
    console.log("Raw form data:", JSON.stringify(data, null, 2));
    console.log("Form data keys:", Object.keys(data));
    console.log("Title value:", data.title);
    console.log("Excerpt value:", data.excerpt);
    console.log("Content value:", data.content);
    console.log("=== FORM SUBMIT DEBUG END ===");
    
    setIsSubmitting(true);
    try {
      await mutation.mutateAsync(data);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6 max-h-[80vh] overflow-y-auto">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <FormField
            control={form.control}
            name="title"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Title</FormLabel>
                <FormControl>
                  <Input 
                    placeholder="Enter case study title"
                    {...field}
                    onChange={(e) => {
                      field.onChange(e);
                      // Auto-generate slug from title
                      if (!isEditing) {
                        const slug = e.target.value
                          .toLowerCase()
                          .replace(/[^a-z0-9]+/g, '-')
                          .replace(/(^-|-$)/g, '');
                        form.setValue('slug', slug);
                      }
                    }}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="slug"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Slug</FormLabel>
                <FormControl>
                  <Input placeholder="case-study-url-slug" {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>

        <FormField
          control={form.control}
          name="excerpt"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Excerpt</FormLabel>
              <FormControl>
                <Textarea 
                  placeholder="Brief description of the case study"
                  className="min-h-[80px]"
                  {...field}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="content"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Content</FormLabel>
              <FormControl>
                <SimpleRichTextEditor
                  value={field.value}
                  onChange={field.onChange}
                  placeholder="Write your case study content here..."
                  className="min-h-[300px]"
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="featuredImage"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Featured Image</FormLabel>
              <FormControl>
                <div className="space-y-4">
                  {/* Image Upload Area */}
                  <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center">
                    <input
                      type="file"
                      accept="image/jpeg,image/jpg,image/png,image/webp"
                      onChange={async (e) => {
                        const file = e.target.files?.[0];
                        if (file) {
                          try {
                            const formData = new FormData();
                            formData.append('image', file);
                            
                            console.log('Uploading file:', file.name, file.size);
                            
                            const response = await fetch('/api/upload/case-study-image', {
                              method: 'POST',
                              body: formData
                            });
                            
                            console.log('Upload response status:', response.status);
                            
                            if (response.ok) {
                              const result = await response.json();
                              console.log('Upload result:', result);
                              field.onChange(result.url);
                              console.log('Image uploaded successfully');
                            } else {
                              const errorText = await response.text();
                              console.error('Upload failed:', response.status, errorText);
                              alert(`Upload failed: ${response.status}. Please try again.`);
                              throw new Error(`Upload failed: ${response.status}`);
                            }
                          } catch (error) {
                            console.error('Upload error:', error);
                          }
                        }
                      }}
                      className="hidden"
                      id="featured-image-upload"
                    />
                    <label
                      htmlFor="featured-image-upload"
                      className="cursor-pointer"
                    >
                      <div className="space-y-2">
                        <div className="text-2xl">📁</div>
                        <div className="text-sm text-gray-600">
                          <span className="font-medium text-blue-600 hover:text-blue-500">
                            Click to upload
                          </span> or drag and drop
                        </div>
                        <div className="text-xs text-gray-500">
                          Recommended: 1200×630 pixels (16:9 ratio)
                          <br />
                          PNG, JPG, WebP up to 2MB
                        </div>
                      </div>
                    </label>
                  </div>
                  
                  {/* Manual URL input as fallback */}
                  <div className="text-sm">
                    <details className="cursor-pointer">
                      <summary className="text-gray-500 hover:text-gray-700">
                        Or enter image URL manually
                      </summary>
                      <div className="mt-2">
                        <Input 
                          placeholder="https://example.com/image.jpg" 
                          value={field.value || ""}
                          onChange={field.onChange}
                        />
                      </div>
                    </details>
                  </div>

                  {/* Show uploaded image preview */}
                  {field.value && (
                    <div className="mt-4 relative">
                      <img 
                        src={field.value} 
                        alt="Featured image preview"
                        className="w-full h-48 object-cover rounded-md border"
                        onError={(e) => {
                          console.error('Image failed to load:', field.value);
                          console.error('Error details:', e);
                        }}
                        onLoad={() => {
                          console.log('Image loaded successfully:', field.value);
                        }}
                      />
                      <Button
                        type="button"
                        variant="destructive"
                        size="sm"
                        className="absolute top-2 right-2"
                        onClick={() => field.onChange('')}
                      >
                        Remove
                      </Button>
                    </div>
                  )}
                </div>
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <FormField
            control={form.control}
            name="authorName"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Author Name</FormLabel>
                <FormControl>
                  <Input placeholder="SAASGAMA Team" {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="authorEmail"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Author Email</FormLabel>
                <FormControl>
                  <Input placeholder="support@saasgama.com" {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>

        <div>
          <label className="text-sm font-medium">Tags</label>
          <Input
            placeholder="Enter tags separated by commas"
            value={tagsInput}
            onChange={(e) => setTagsInput(e.target.value)}
            className="mt-1"
          />
        </div>

        <div className="rounded-lg border border-dashed border-blue-200 bg-blue-50/40 p-4 space-y-4">
          <p className="text-sm font-semibold text-blue-700">SEO Meta Tags</p>
          <FormField
            control={form.control}
            name="metaTitle"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Meta Title <span className="text-gray-400 font-normal">(50–60 chars recommended)</span></FormLabel>
                <FormControl>
                  <Input placeholder="SEO title shown in search results" {...field} />
                </FormControl>
                <div className="text-xs text-gray-400 text-right">{field.value?.length || 0} chars</div>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="metaDescription"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Meta Description <span className="text-gray-400 font-normal">(150–160 chars recommended)</span></FormLabel>
                <FormControl>
                  <Textarea placeholder="SEO description shown in search results" className="min-h-[80px]" {...field} />
                </FormControl>
                <div className="text-xs text-gray-400 text-right">{field.value?.length || 0} chars</div>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>

        <FormField
          control={form.control}
          name="isPublished"
          render={({ field }) => (
            <FormItem className="flex items-center justify-between rounded-lg border p-4">
              <div className="space-y-0.5">
                <FormLabel className="text-base">Publish Case Study</FormLabel>
                <div className="text-sm text-gray-600">
                  Make this case study visible on the website
                </div>
              </div>
              <FormControl>
                <Switch
                  checked={field.value}
                  onCheckedChange={field.onChange}
                />
              </FormControl>
            </FormItem>
          )}
        />

        <div className="flex justify-end gap-3">
          <Button type="button" variant="outline" onClick={onCancel}>
            Cancel
          </Button>
          <Button type="submit" disabled={isSubmitting}>
            {isSubmitting ? "Saving..." : isEditing ? "Update Case Study" : "Create Case Study"}
          </Button>
        </div>
      </form>
    </Form>
  );
}

function CaseStudyManagementContent() {
  const [selectedCaseStudy, setSelectedCaseStudy] = useState<CaseStudy | null>(null);
  const [isFormOpen, setIsFormOpen] = useState(false);
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const { data: caseStudies = [], isLoading } = useQuery<CaseStudy[]>({
    queryKey: ["/api/admin/case-studies"],
  });

  const deleteMutation = useMutation({
    mutationFn: (id: number) => apiRequest("DELETE", `/api/admin/case-studies/${id}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/admin/case-studies"] });
      toast({
        title: "Case study deleted",
        description: "Case study has been deleted successfully.",
      });
    },
    onError: (error: any) => {
      toast({
        title: "Error",
        description: error.message || "Failed to delete case study",
        variant: "destructive",
      });
    },
  });

  const handleEdit = (caseStudy: CaseStudy) => {
    setSelectedCaseStudy(caseStudy);
    setIsFormOpen(true);
  };

  const handleDelete = async (id: number) => {
    if (confirm("Are you sure you want to delete this case study?")) {
      deleteMutation.mutate(id);
    }
  };

  const handleFormSuccess = () => {
    setIsFormOpen(false);
    setSelectedCaseStudy(null);
  };

  const handleFormCancel = () => {
    setIsFormOpen(false);
    setSelectedCaseStudy(null);
  };

  const formatDate = (date: string | Date) => {
    return new Date(date).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin w-8 h-8 border-4 border-primary border-t-transparent rounded-full" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold">Case Study Management</h1>
          <p className="text-gray-600">Create and manage case studies for your website</p>
        </div>
        
        <Dialog open={isFormOpen} onOpenChange={setIsFormOpen}>
          <DialogTrigger asChild>
            <Button onClick={() => setSelectedCaseStudy(null)}>
              <Plus className="h-4 w-4 mr-2" />
              New Case Study
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-4xl max-h-[90vh] overflow-hidden">
            <DialogHeader>
              <DialogTitle>
                {selectedCaseStudy ? "Edit Case Study" : "Create New Case Study"}
              </DialogTitle>
            </DialogHeader>
            <CaseStudyForm
              caseStudy={selectedCaseStudy || undefined}
              onSuccess={handleFormSuccess}
              onCancel={handleFormCancel}
            />
          </DialogContent>
        </Dialog>
      </div>

      <div className="grid gap-6">
        {caseStudies.length === 0 ? (
          <Card>
            <CardContent className="flex flex-col items-center justify-center py-16">
              <div className="text-4xl mb-4">📄</div>
              <h3 className="text-lg font-semibold mb-2">No case studies yet</h3>
              <p className="text-gray-600 text-center mb-4">
                Create your first case study to showcase your success stories
              </p>
              <Button onClick={() => setIsFormOpen(true)}>
                <Plus className="h-4 w-4 mr-2" />
                Create First Case Study
              </Button>
            </CardContent>
          </Card>
        ) : (
          <div className="grid gap-4">
            {caseStudies.map((caseStudy: CaseStudy) => (
              <Card key={caseStudy.id} className="hover:shadow-md transition-shadow">
                <CardHeader className="pb-3">
                  <div className="flex justify-between items-start">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-2">
                        <CardTitle className="text-lg">{caseStudy.title}</CardTitle>
                        <Badge variant={caseStudy.isPublished ? "default" : "secondary"}>
                          {caseStudy.isPublished ? "Published" : "Draft"}
                        </Badge>
                      </div>
                      
                      <p className="text-sm text-gray-600 mb-3 line-clamp-2">
                        {caseStudy.excerpt}
                      </p>
                      
                      <div className="flex items-center gap-4 text-xs text-gray-500">
                        <div className="flex items-center gap-1">
                          <User className="h-3 w-3" />
                          {caseStudy.authorName}
                        </div>
                        <div className="flex items-center gap-1">
                          <Calendar className="h-3 w-3" />
                          {formatDate(caseStudy.createdAt)}
                        </div>
                        {caseStudy.tags && caseStudy.tags.length > 0 && (
                          <div className="flex items-center gap-1">
                            <Tag className="h-3 w-3" />
                            {caseStudy.tags.slice(0, 2).join(", ")}
                            {caseStudy.tags.length > 2 && " +more"}
                          </div>
                        )}
                      </div>
                    </div>
                    
                    {caseStudy.featuredImage && (
                      <img 
                        src={caseStudy.featuredImage} 
                        alt={caseStudy.title}
                        className="w-20 h-20 object-cover rounded-md ml-4"
                      />
                    )}
                  </div>
                </CardHeader>
                
                <CardContent className="pt-0">
                  <div className="flex justify-between items-center">
                    <div className="text-xs text-gray-500">
                      Slug: /{caseStudy.slug}
                    </div>
                    
                    <div className="flex gap-2">
                      {caseStudy.isPublished && (
                        <Button variant="outline" size="sm" asChild>
                          <a href={`/case-studies/${caseStudy.slug}`} target="_blank">
                            <ExternalLink className="h-3 w-3 mr-1" />
                            View
                          </a>
                        </Button>
                      )}
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleEdit(caseStudy)}
                      >
                        <Edit className="h-3 w-3 mr-1" />
                        Edit
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleDelete(caseStudy.id)}
                        className="text-red-600 hover:text-red-700"
                      >
                        <Trash2 className="h-3 w-3 mr-1" />
                        Delete
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

export default function CaseStudyManagement() {
  return (
    <SuperAdminLayout>
      <CaseStudyManagementContent />
    </SuperAdminLayout>
  );
}